/**
 * Org structure — the details popup.
 *
 * Everything the chart's card cannot fit: the lineage, the officer, the six
 * figures, the people actually posted to the unit, and one click to any
 * neighbour up or down.
 *
 * It is a popup rather than a panel pinned inside the chart for two reasons.
 * A panel pinned over the plane is a child of the pannable stage, so its
 * scrollbar pans the chart and its wheel zooms it — the screen simply stops
 * responding once it is up. And one popup serves all five views, so picking a
 * unit means the same thing wherever you picked it.
 *
 * The roster is the one part not built from the units payload: personnel
 * membership lives on `personnel_master.unitId`, and nothing else on this
 * screen reads it. A headcount is a claim about that list, so the panel prints
 * the list underneath it.
 */
import { useCallback, useRef, useState } from 'react';
import Icon from './org-icons';
import OrgFlow from './org-flow';
import {
  avatarStyle,
  initials,
  headColour,
  headLabel,
  nf,
  vacancyPct,
  vacancyTone,
  type OrgModel,
  type OrgNode,
} from './org-model';
import styles from './org-structure.module.css';

interface InspectorProps {
  model: OrgModel;
  unit: OrgNode;
  onSelect: (id: string | null) => void;
  stored: boolean;
  onToggleCompare: (id: string) => void;
  onOpenUnit: (id: string) => void;
  onEditUnit?: (id: string) => void;
  onAddUnder?: (id: string) => void;
  onOpenPeople?: (id: string) => void;
}

const Avatar = ({ node }: { node: OrgNode }) => (
  <span className={styles.av} style={avatarStyle(node)} aria-hidden="true">
    {node.head ? initials(node.head.name) : '—'}
  </span>
);

const OrgInspector = ({
  model,
  unit: sel,
  onSelect,
  stored,
  onToggleCompare,
  onOpenUnit,
  onEditUnit,
  onAddUnder,
  onOpenPeople,
}: InspectorProps) => {

  /*
   * The figures that have something behind them.
   *
   * Every one of these was printed unconditionally, and on a police station
   * every one of them read 0, 0 / 0 or an em dash — six labels and six zeroes
   * saying nothing, on the best space in the panel. A zero here is not a
   * measurement of the force; it is the absence of a record. So a figure is
   * drawn when it has a value and left out when it has not, and if none of them
   * do, the grid itself does not appear.
   */
  const figs: Array<{ k: string; label: string; value: React.ReactNode; tone?: string }> = [];
  if (model.hasHeadcount) {
    figs.push({ k: 'people', label: 'People working', value: nf(sel.direct) });
  }
  if (sel.rPosts > 0) {
    const pct = vacancyPct(sel);
    figs.push({
      k: 'filled',
      label: 'Posts filled',
      value: (
        <>
          {nf(sel.rFilled)}
          <small> / {nf(sel.rPosts)}</small>
        </>
      ),
    });
    figs.push({ k: 'unfilled', label: 'Unfilled', value: pct === null ? '—' : `${pct}%`, tone: vacancyTone(pct) });
  }
  if (sel.subUnits > 0) figs.push({ k: 'subs', label: 'Sub-units', value: nf(sel.subUnits) });

  /* An officer line where an officer is on record, or where a head post exists
     and stands empty — a real vacancy is worth saying. "Officer not recorded"
     on a unit that never had a head post on file is not. */
  const showOfficer = !!sel.head || sel.headState === 'vacant';

  /** The unit's own phone field, which older records hold as a bare string. */
  const phones = (Array.isArray(sel.raw.phone) ? sel.raw.phone : sel.raw.phone ? [sel.raw.phone] : []).filter(Boolean);
  /* "Guntur" alone in the title bar could be the unit's own name; "Guntur
     district" cannot. Units whose district name already carries the word are
     left as they are, so nothing reads "Guntur District district". */
  const districtPhrase =
    // A unit that IS a district does not need telling which district it is in.
    !sel.districtName || /district/i.test(sel.typeLabel)
      ? ''
      : /district/i.test(sel.districtName)
        ? sel.districtName
        : `${sel.districtName} district`;

  const since = sel.raw.createdAt && !Number.isNaN(new Date(sel.raw.createdAt).getTime())
    ? new Date(sel.raw.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  /*
   * Dragging the panel by its header.
   *
   * The popup covers the middle of the canvas, which is exactly where the unit
   * you just clicked tends to be — so reading the details means losing sight of
   * the thing they are about. Rather than shrink the panel or move it to a
   * corner and hope that suits everyone, it moves: pick it up by the header and
   * put it where the chart is not.
   *
   * An offset on top of the backdrop's centring, not a switch to absolute
   * positioning, so a window resize still re-centres a panel nobody has moved.
   * The offset is clamped on pointerdown against the backdrop's own box: the
   * panel can go right to the edges but never past them, because a dialog you
   * have to drag back from off-screen is worse than one that would not move.
   */
  const rootRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    px: number; py: number; ox: number; oy: number;
    minX: number; maxX: number; minY: number; maxY: number;
  } | null>(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const moved = pos.x !== 0 || pos.y !== 0;

  /**
   * How far the panel may be offset, measured from where it is right now.
   *
   * Read off the DOM rather than tracked, because the panel's size changes with
   * its content — a station with two rows and a district with twenty do not get
   * the same limits, and a stale bound is how a dialog ends up off-screen.
   */
  const limitsOf = useCallback((at: { x: number; y: number }) => {
    const el = rootRef.current;
    const area = el?.parentElement;
    if (!el || !area) return null;
    const box = el.getBoundingClientRect();
    const bounds = area.getBoundingClientRect();
    /** How much of the panel must stay inside, so there is always a handle. */
    const KEEP = 72;
    return {
      minX: at.x - (box.right - bounds.left - KEEP),
      maxX: at.x + (bounds.right - KEEP - box.left),
      minY: at.y - (box.top - bounds.top),
      maxY: at.y + (bounds.bottom - KEEP - box.top),
    };
  }, []);

  const onHeadDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // The header carries the close and compare buttons; they stay buttons.
      if ((e.target as HTMLElement).closest('button')) return;
      const lim = limitsOf(pos);
      if (!lim) return;
      drag.current = { px: e.clientX, py: e.clientY, ox: pos.x, oy: pos.y, ...lim };
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(true);
    },
    [pos, limitsOf]
  );

  const onHeadMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
    setPos({
      x: clamp(d.ox + e.clientX - d.px, d.minX, d.maxX),
      y: clamp(d.oy + e.clientY - d.py, d.minY, d.maxY),
    });
  }, []);

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  /** Arrow keys move it too, against the same limits the pointer gets. */
  const onHeadKey = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Home') {
        e.preventDefault();
        setPos({ x: 0, y: 0 });
        return;
      }
      const step = e.shiftKey ? 40 : 12;
      const by: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
      };
      const d = by[e.key];
      if (!d) return;
      e.preventDefault();
      setPos((p) => {
        const lim = limitsOf(p);
        const next = { x: p.x + d[0], y: p.y + d[1] };
        if (!lim) return next;
        return {
          x: Math.min(lim.maxX, Math.max(lim.minX, next.x)),
          y: Math.min(lim.maxY, Math.max(lim.minY, next.y)),
        };
      });
    },
    [limitsOf]
  );

  return (
    <div
      ref={rootRef}
      className={styles.insp}
      role="dialog"
      aria-modal="true"
      aria-label={`${sel.name} — unit details`}
      style={moved ? { transform: `translate(${pos.x}px, ${pos.y}px)` } : undefined}
    >
      {/* The same level colour the unit's card carries, so the popup is
          visibly about the block you just clicked. */}
      <span className={styles.popstrip} style={{ background: headColour(sel) }} />
      {/* The whole header is the handle. No grip icon: a header that says
          "grab me" with the cursor costs nothing, and one more control in a row
          that already has four would cost more than it is worth. */}
      <div
        className={`${styles.ihd} ${styles.ihdrag}${dragging ? ` ${styles.ihdragging}` : ''}`}
        onPointerDown={onHeadDown}
        onPointerMove={onHeadMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={() => setPos({ x: 0, y: 0 })}
        onKeyDown={onHeadKey}
        tabIndex={0}
        role="group"
        aria-label="Move this panel — drag, or use the arrow keys. Home puts it back."
        title="Drag to move · double-click to put it back"
      >
        <div style={{ minWidth: 0 }}>
          <h3>{sel.name}</h3>
          {/* What this unit IS, in the order somebody would say it out loud.
              It used to read "GNT-PS-01 · POLICE STATION": the internal code
              first, in a monospaced face, and the one genuinely useful word
              shouting in capitals behind it. The kind of unit leads now, in
              ordinary sentence case, the district follows because that is what
              tells two similarly named stations apart, and the code comes last
              — labelled, so it reads as a reference rather than a riddle. */}
          <p className={styles.usub}>
            <span>{sel.typeLabel}</span>
            {districtPhrase && <span>{districtPhrase}</span>}
            <span>
              Code <b className={styles.mono}>{sel.code}</b>
            </span>
          </p>
        </div>
        <span className={styles.rowact} style={{ marginLeft: 'auto' }}>
          <button
            type="button"
            className={`${styles.iact}${stored ? ` ${styles.iacton}` : ''}`}
            title={stored ? 'Remove from the compare set' : 'Add to the compare set'}
            onClick={() => onToggleCompare(sel.id)}
          >
            <Icon name="cols" className={styles.ic} size={14} />
          </button>
          {onEditUnit && (
            <button aria-label="Edit this unit" type="button" className={styles.iact} title="Edit this unit" onClick={() => onEditUnit(sel.id)}>
              <Icon name="edit" className={styles.ic} size={14} />
            </button>
          )}
          {onAddUnder && (
            <button aria-label="Add a unit under this one"
              type="button"
              className={styles.iact}
              title="Add a unit under this one"
              onClick={() => onAddUnder(sel.id)}
            >
              <Icon name="plus" className={styles.ic} size={14} />
            </button>
          )}
          <button
            type="button"
            className={styles.iact}
            title="Close (Esc)"
            aria-label="Close"
            onClick={() => onSelect(null)}
          >
            <Icon name="x" className={styles.ic} size={14} />
          </button>
        </span>
      </div>
      <div className={styles.ibody}>
        {/* The diagram the chart behind cannot draw at this size: straight
            down the command, no panning, every step a place you can go. */}
        <div className={styles.fcol}>
          <h4 className={styles.lbl}>Hierarchy</h4>
          <OrgFlow model={model} unit={sel} onSelect={(id) => onSelect(id)} />
        </div>

        <div className={styles.dcol}>
          {showOfficer && (
            <div className={styles.cmdr}>
              <Avatar node={sel} />
              <span>
                <span
                  className={styles.n}
                  style={sel.headState === 'vacant' ? { color: 'var(--org-danger)' } : undefined}
                >
                  {headLabel(sel)}
                </span>
                {/* The post and the badge number, whichever of them the unit
                    actually records. Joined rather than concatenated: with the
                    post gone, the old form left a leading separator hanging in
                    front of the badge. */}
                {(sel.headRank || sel.head?.badge) && (
                  <span className={styles.r}>
                    {[sel.headRank, sel.head?.badge].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
            </div>
          )}

          {figs.length > 0 && (
            <div className={styles.figs2}>
              {figs.map((f) => (
                <div key={f.k} className={styles.fg2}>
                  <b>{f.label}</b>
                  <span style={f.tone ? { color: f.tone } : undefined}>
                    {f.value}
                    {f.k === 'people' && onOpenPeople && (
                      <button
                        type="button"
                        className={styles.peopleLink}
                        title="View people working in this unit"
                        aria-label={`View ${nf(sel.direct)} people working in ${sel.name}`}
                        onClick={() => onOpenPeople(sel.id)}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* The unit's own record — what the row actually carries.
              This list was four rows long and two of them were the officer's
              contact details, so a station with no officer showed almost
              nothing. Everything the document stores is here now, and a field
              with nothing in it is left out rather than dashed. */}
          <div>
            <h4 className={styles.lbl}>Unit record</h4>
            <dl className={styles.dl}>
              <dt>Unit code</dt>
              <dd className={styles.mono}>{sel.code}</dd>
              <dt>Type</dt>
              <dd>{sel.typeLabel}</dd>
              {sel.departmentName && (
                <>
                  <dt>Department</dt>
                  <dd>{sel.departmentName}</dd>
                </>
              )}
              {sel.districtName && (
                <>
                  <dt>District</dt>
                  <dd>{sel.districtName}</dd>
                </>
              )}
              {sel.raw.city && (
                <>
                  <dt>City</dt>
                  <dd>{sel.raw.city}</dd>
                </>
              )}
              {sel.raw.email && (
                <>
                  <dt>Unit email</dt>
                  <dd>{sel.raw.email}</dd>
                </>
              )}
              {phones.length > 0 && (
                <>
                  <dt>Unit phone</dt>
                  <dd className={styles.mono}>{phones.join(', ')}</dd>
                </>
              )}
              {sel.head?.email && (
                <>
                  <dt>Officer email</dt>
                  <dd>{sel.head.email}</dd>
                </>
              )}
              {sel.head?.phone && (
                <>
                  <dt>Officer phone</dt>
                  <dd className={styles.mono}>{sel.head.phone}</dd>
                </>
              )}
              {!sel.isActive && (
                <>
                  <dt>Status</dt>
                  <dd className={styles.vacname}>Inactive</dd>
                </>
              )}
              {since && (
                <>
                  <dt>On record since</dt>
                  <dd>{since}</dd>
                </>
              )}
            </dl>
          </div>

          <button aria-label="Leave the chart and open this unit's own record"
            type="button"
            className={`${styles.btn} ${styles.pri}`}
            title="Leave the chart and open this unit's own record"
            onClick={() => onOpenUnit(sel.id)}
          >
            <Icon name="out" className={styles.ic} size={14} />
            Open the unit record
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgInspector;
